// SCR-052 (S2.4.7) — Vai trò & phạm vi: persona NCC_ADMIN tìm một tài khoản trong scope,
// đánh giá quyền hiện hành rồi cấp/thu hồi đúng phạm vi và thời gian. Layout theo spec
// hợp nhất §8: PageHeading + bộ chọn tài khoản + bảng assignment trung tâm; preview/lịch sử
// trong Drawer (PROGRESSIVE); grant/revoke trong Modal. Mọi quyết định quyền thật nằm ở
// backend — visibility ở đây chỉ là UX hint theo IRM đã duyệt.
import {
  Alert,
  App as AntdApp,
  Avatar,
  Button,
  Card,
  Empty,
  Input,
  Result,
  Select,
  Skeleton,
  Tag,
  Typography,
} from "antd";
import SearchOutlined from "@ant-design/icons/SearchOutlined";
import UserOutlined from "@ant-design/icons/UserOutlined";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ApiContractError, ApiProblem, createRequestId } from "../../api/httpClient";
import { useActorContext } from "../../auth/ActorContext";
import { PageHeading } from "../../components/PageHeading";
import { getAdministrativeAreaTree } from "../administration/administrativeAreaAdminApi";
import { getOrganizationTree } from "../administration/organizationAdminApi";
import { describeAdminError } from "../administration/commandErrors";
import { getUserAccount, listUserAccounts } from "../user-accounts/userAccountAdminApi";
import type { UserAccountAdminListItem } from "../user-accounts/userAccountAdminApi";
import { EffectivePermissionDrawer } from "./EffectivePermissionDrawer";
import type { DrawerMode, PreviewState } from "./EffectivePermissionDrawer";
import { GrantRoleAssignmentModal } from "./GrantRoleAssignmentModal";
import type { GrantModalError } from "./GrantRoleAssignmentModal";
import { RevokeRoleAssignmentModal } from "./RevokeRoleAssignmentModal";
import type { RevokeModalError } from "./RevokeRoleAssignmentModal";
import { RoleAssignmentTable } from "./RoleAssignmentTable";
import {
  actorHasAction,
  formatAssignmentDate,
  getCurrentAuthorizationCatalog,
  grantUserRoleAssignment,
  listUserRoleAssignments,
  previewUserEffectivePermissions,
  revokeUserRoleAssignment,
  ROLE_ASSIGNMENT_GRANT_ACTION,
  ROLE_ASSIGNMENT_READ_ACTION,
  ROLE_ASSIGNMENT_REVOKE_ACTION,
  scopeSummary,
} from "./roleAssignmentAdminApi";
import type {
  AuthorizationCatalogPublished,
  GrantRoleAssignmentInput,
  RoleAssignmentItem,
  RoleAssignmentPage,
} from "./roleAssignmentAdminApi";
import type { OrganizationTreeNode } from "../administration/organizationAdminApi";
import type { AdministrativeAreaTreeNode } from "../administration/administrativeAreaAdminApi";
import "./role-assignments.css";

const NCC_ADMIN_ROLE = "NCC_ADMIN";
const LIST_SIZE = 20;

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}

function requestIdOf(error: unknown): string | undefined {
  if (error instanceof ApiProblem) return error.problem.requestId;
  if (error instanceof ApiContractError) return error.requestId;
  return undefined;
}

function errorCodeOf(error: unknown): string {
  return error instanceof ApiProblem ? error.problem.code : "UNKNOWN";
}

export function RoleAssignmentsPage() {
  const state = useActorContext();
  if (state.status === "loading" || state.status === "refreshing") {
    return (
      <div className="role-assignments-page" role="status" aria-label="Đang tải phiên làm việc">
        <Skeleton active paragraph={{ rows: 8 }} />
      </div>
    );
  }
  const isAdmin = state.status === "ready" || state.status === "stale";
  const hasAdminRole = isAdmin && state.actor.grants.some((grant) => grant.roleCode === NCC_ADMIN_ROLE);
  if (!hasAdminRole) {
    return (
      <Result
        status="403"
        title="Bạn không có quyền quản trị phân quyền"
        subTitle="Màn hình này chỉ dành cho tài khoản quản trị được hệ thống ghi nhận."
      />
    );
  }
  return (
    <AntdApp>
      <RoleAssignmentsContent />
    </AntdApp>
  );
}

type CatalogState =
  | { status: "loading" }
  | { status: "ready"; catalog: AuthorizationCatalogPublished }
  | { status: "error"; requestId?: string | undefined };

type AccountsState =
  | { status: "loading" }
  | { status: "ready"; items: readonly UserAccountAdminListItem[] }
  | { status: "error"; requestId?: string | undefined };

type AssignmentsState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; page: RoleAssignmentPage }
  | { status: "forbidden" }
  | { status: "error"; code: string; requestId?: string | undefined };

function RoleAssignmentsContent() {
  const { message: messageApi } = AntdApp.useApp();
  const actorState = useActorContext();
  const actorRoleCodes = useMemo(
    () => actorState.status === "ready" || actorState.status === "stale"
      ? actorState.actor.grants.map((grant) => grant.roleCode)
      : [],
    [actorState],
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const urlAccountId = searchParams.get("accountId");
  const [catalogState, setCatalogState] = useState<CatalogState>({ status: "loading" });
  const [accountsState, setAccountsState] = useState<AccountsState>({ status: "loading" });
  const [orgNodes, setOrgNodes] = useState<readonly OrganizationTreeNode[]>([]);
  const [areaNodes, setAreaNodes] = useState<readonly AdministrativeAreaTreeNode[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [dataNonce, setDataNonce] = useState(0);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(() => urlAccountId);
  const [assignmentsState, setAssignmentsState] = useState<AssignmentsState>({ status: "idle" });
  const [reloadNonce, setReloadNonce] = useState(0);
  const [drawerMode, setDrawerMode] = useState<DrawerMode | null>(null);
  const [previewState, setPreviewState] = useState<PreviewState | null>(null);
  const [grantModal, setGrantModal] = useState<
    { submitting: boolean; error: GrantModalError | null } | null>(null);
  const [revokeModal, setRevokeModal] = useState<
    { item: RoleAssignmentItem; submitting: boolean; error: RevokeModalError | null } | null>(null);
  const assignmentsGeneration = useRef(0);

  useEffect(() => {
    if (urlAccountId && urlAccountId !== selectedAccountId) {
      setSelectedAccountId(urlAccountId);
    }
  }, [urlAccountId, selectedAccountId]);

  // Khi selectedAccountId có mà chưa có trong accountsState, nạp chi tiết account đó
  useEffect(() => {
    if (!selectedAccountId) return;
    if (accountsState.status !== "ready") return;
    if (accountsState.items.some((item) => item.id === selectedAccountId)) return;

    const controller = new AbortController();
    void getUserAccount(selectedAccountId, controller.signal)
      .then((detail) => {
        setAccountsState((prev) => {
          if (prev.status !== "ready") return prev;
          if (prev.items.some((item) => item.id === detail.id)) return prev;
          return {
            ...prev,
            items: [
              {
                id: detail.id,
                personDisplayName: detail.personDisplayName,
                username: detail.username,
                homeOrganization: detail.homeOrganization,
                status: detail.status,
                identityLinked: detail.identityLinked,
                version: detail.version,
              },
              ...prev.items,
            ],
          };
        });
      })
      .catch(() => undefined);
    return () => controller.abort();
  }, [selectedAccountId, accountsState]);

  const loadAccounts = useCallback(async (q: string): Promise<void> => {
    setAccountsState({ status: "loading" });
    const trimmed = q.trim();
    try {
      const page = await listUserAccounts(
        trimmed
          ? { page: 0, size: LIST_SIZE, q: trimmed }
          : { page: 0, size: LIST_SIZE },
      );
      setAccountsState({ status: "ready", items: page.data });
    } catch (error) {
      if (isAbortError(error)) return;
      setAccountsState({ status: "error", requestId: requestIdOf(error) });
    }
  }, []);

  // Catalog + danh sách tài khoản + hai cây tên đọc song song ngay khi mount
  // (spec §8.4 — không waterfall trước khi người dùng chọn account).
  useEffect(() => {
    const controller = new AbortController();
    setCatalogState({ status: "loading" });
    void getCurrentAuthorizationCatalog(controller.signal)
      .then((catalog) => setCatalogState({ status: "ready", catalog }))
      .catch((error: unknown) => {
        if (isAbortError(error)) return;
        setCatalogState({ status: "error", requestId: requestIdOf(error) });
      });
    void loadAccounts("");
    void getOrganizationTree(undefined, controller.signal)
      .then(setOrgNodes)
      .catch(() => undefined);
    void getAdministrativeAreaTree(undefined, controller.signal)
      .then(setAreaNodes)
      .catch(() => undefined);
    return () => {
      assignmentsGeneration.current += 1;
      controller.abort();
    };
  }, [dataNonce, loadAccounts]);

  const selectedAccount = useMemo(
    () => accountsState.status === "ready"
      ? accountsState.items.find((item) => item.id === selectedAccountId) ?? null
      : null,
    [accountsState, selectedAccountId],
  );

  const orgNames = useMemo(() => new Map(orgNodes.map((node) => [node.id, node.name])), [orgNodes]);
  const areaNames = useMemo(() => new Map(areaNodes.map((node) => [node.id, node.name])), [areaNodes]);
  const catalog = catalogState.status === "ready" ? catalogState.catalog : null;
  const canGrant = catalog != null
    && actorHasAction(catalog.roles, actorRoleCodes, ROLE_ASSIGNMENT_GRANT_ACTION);
  const canRevoke = catalog != null
    && actorHasAction(catalog.roles, actorRoleCodes, ROLE_ASSIGNMENT_REVOKE_ACTION);
  const canRead = catalog != null
    && actorHasAction(catalog.roles, actorRoleCodes, ROLE_ASSIGNMENT_READ_ACTION);
  const roleLabel = useCallback((code: string): string =>
    catalog?.roles.find((role) => role.code === code)?.displayName
      ?? "Vai trò không còn trong danh mục",
  [catalog]);

  // Assignments chỉ fetch SAU khi chọn tài khoản; response cũ bị abort theo generation.
  useEffect(() => {
    if (selectedAccountId == null) {
      setAssignmentsState({ status: "idle" });
      return;
    }
    const generation = assignmentsGeneration.current;
    const controller = new AbortController();
    setAssignmentsState({ status: "loading" });
    void listUserRoleAssignments(selectedAccountId, { page: 0, size: LIST_SIZE }, controller.signal)
      .then((page) => {
        if (assignmentsGeneration.current === generation) {
          setAssignmentsState({ status: "ready", page });
        }
      })
      .catch((error: unknown) => {
        if (isAbortError(error) || assignmentsGeneration.current !== generation) return;
        if (errorCodeOf(error) === "COMMON_FORBIDDEN_SCOPE") {
          setAssignmentsState({ status: "forbidden" });
          return;
        }
        setAssignmentsState({
          status: "error",
          code: errorCodeOf(error),
          requestId: requestIdOf(error),
        });
      });
    return () => {
      assignmentsGeneration.current += 1;
      controller.abort();
    };
  }, [selectedAccountId, reloadNonce]);

  // Preview chỉ fetch khi drawer mở ở chế độ preview (PROGRESSIVE).
  useEffect(() => {
    if (drawerMode !== "preview" || selectedAccountId == null) {
      return;
    }
    const controller = new AbortController();
    setPreviewState({ status: "loading" });
    void previewUserEffectivePermissions(selectedAccountId, undefined, controller.signal)
      .then((preview) => setPreviewState({ status: "ready", preview }))
      .catch((error: unknown) => {
        if (isAbortError(error)) return;
        setPreviewState({
          status: "error",
          message: describeAdminError(errorCodeOf(error)),
          requestId: requestIdOf(error),
        });
      });
    return () => controller.abort();
  }, [drawerMode, selectedAccountId, reloadNonce]);

  const reloadAssignments = useCallback((): void => setReloadNonce((value) => value + 1), []);

  const handleGrantSubmit = useCallback(async (values: GrantRoleAssignmentInput): Promise<void> => {
    if (selectedAccountId == null) return;
    setGrantModal((current) => ({ submitting: true, error: current?.error ?? null }));
    try {
      await grantUserRoleAssignment(selectedAccountId, values, createRequestId());
      messageApi.success("Đã cấp vai trò");
      setGrantModal(null);
      reloadAssignments();
    } catch (error) {
      setGrantModal({
        submitting: false,
        error: { code: errorCodeOf(error), requestId: requestIdOf(error) },
      });
    }
  }, [messageApi, reloadAssignments, selectedAccountId]);

  const handleRevokeConfirm = useCallback(async (reason: string): Promise<void> => {
    if (selectedAccountId == null || revokeModal == null) return;
    setRevokeModal((current) => current && { ...current, submitting: true, error: null });
    try {
      await revokeUserRoleAssignment(
        selectedAccountId,
        revokeModal.item.assignmentId,
        { expectedVersion: revokeModal.item.version, reason },
        createRequestId(),
      );
      messageApi.success("Đã thu hồi vai trò");
      setRevokeModal(null);
      reloadAssignments();
    } catch (error) {
      setRevokeModal((current) => current && {
        ...current,
        submitting: false,
        error: { code: errorCodeOf(error), requestId: requestIdOf(error) },
      });
    }
  }, [messageApi, reloadAssignments, revokeModal, selectedAccountId]);

  const handleAccountSelect = useCallback((value: string) => {
    setSelectedAccountId(value);
    setDrawerMode(null);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("accountId", value);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const periodLabel = (item: RoleAssignmentItem): string =>
    `${formatAssignmentDate(item.validFrom)} → ${item.validTo ? formatAssignmentDate(item.validTo) : "vô thời hạn"}`;

  return (
    <div className="role-assignments-page">
      <PageHeading
        title="Vai trò & phạm vi"
        description="Cấp và thu hồi vai trò cho tài khoản trong phạm vi bạn được quản lý."
      />

      {catalogState.status === "error" && (
        <Alert
          type="error"
          showIcon
          role="alert"
          message="Không tải được danh mục quyền"
          description={catalogState.requestId ? `Mã hỗ trợ: ${catalogState.requestId}` : undefined}
          action={<Button size="small" onClick={() => setDataNonce((value) => value + 1)}>Thử lại</Button>}
        />
      )}

      <Card className="role-assignments-filter-card" variant="borderless">
        <div className="role-assignments-toolbar" role="search" aria-label="Tìm và chọn tài khoản">
          <div className="role-assignments-search-field">
            <Input
              aria-label="Tìm theo tên hoặc tên đăng nhập"
              placeholder="Tìm theo tên hoặc tên đăng nhập"
              prefix={<SearchOutlined style={{ color: "var(--ncc-color-text-secondary)" }} />}
              allowClear
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              onPressEnter={() => void loadAccounts(searchTerm)}
            />
            <Button onClick={() => void loadAccounts(searchTerm)}>Tìm tài khoản</Button>
          </div>
          <div className="role-assignments-select-field">
            <Select
              aria-label="Chọn tài khoản cần quản trị"
              className="role-assignments-account-select"
              placeholder="Chọn tài khoản"
              showSearch
              optionFilterProp="label"
              virtual={false}
              value={selectedAccountId}
              onChange={handleAccountSelect}
              loading={accountsState.status === "loading"}
              options={accountsState.status === "ready"
                ? accountsState.items.map((item) => ({
                  value: item.id,
                  label: `${item.personDisplayName} · ${item.username}`,
                }))
                : []}
            />
          </div>
        </div>
      </Card>

      {selectedAccount == null && accountsState.status === "ready" && (
        <Card className="role-assignments-empty-card" variant="borderless">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Chọn một tài khoản để xem phân quyền hiện hành."
          />
        </Card>
      )}

      {selectedAccount != null && (
        <Card
          className="role-assignments-user-card"
          variant="borderless"
          role="region"
          aria-label="Thông tin tài khoản được chọn"
        >
          <div className="role-assignments-user-header">
            <div className="role-assignments-user-info">
              <Avatar
                size={44}
                icon={<UserOutlined />}
                className="role-assignments-user-avatar"
              />
              <div className="role-assignments-user-details">
                <div className="role-assignments-user-name-row">
                  <Typography.Text strong style={{ fontSize: "16px" }}>
                    {selectedAccount.personDisplayName}
                  </Typography.Text>
                  <Typography.Text type="secondary" className="role-assignments-user-login">
                    · {selectedAccount.username}
                  </Typography.Text>
                  <Tag color={selectedAccount.status === "ACTIVE" ? "success" : "warning"}>
                    {selectedAccount.status === "ACTIVE" ? "Đang hoạt động" : "Tạm ngưng"}
                  </Tag>
                </div>
                <div className="role-assignments-user-sub-row">
                  <span className="role-assignments-account-summary">
                    <Typography.Text type="secondary">
                      {selectedAccount.homeOrganization.name}
                    </Typography.Text>
                  </span>
                </div>
              </div>
            </div>
            <div className="role-assignments-actions">
              {canGrant && selectedAccount.status === "ACTIVE" && (
                <Button
                  type="primary"
                  onClick={() => setGrantModal({ submitting: false, error: null })}
                >
                  Cấp vai trò
                </Button>
              )}
              {canRead && (
                <>
                  <Button onClick={() => setDrawerMode("preview")}>Xem quyền hiệu lực</Button>
                  <Button onClick={() => setDrawerMode("history")}>Xem lịch sử</Button>
                </>
              )}
            </div>
          </div>
        </Card>
      )}

      {selectedAccountId != null && assignmentsState.status === "loading" && (
        <Card className="role-assignments-table-card" variant="borderless">
          <Skeleton active paragraph={{ rows: 4 }} />
        </Card>
      )}

      {selectedAccountId != null && assignmentsState.status === "forbidden" && (
        <Result
          status="403"
          title="Bạn không có quyền với phạm vi dữ liệu này"
          subTitle="Không thể xem phân quyền của tài khoản ngoài phạm vi quản lý."
        />
      )}

      {selectedAccountId != null && assignmentsState.status === "error" && (
        <Alert
          type="error"
          showIcon
          role="alert"
          message={describeAdminError(assignmentsState.code)}
          description={assignmentsState.requestId ? `Mã hỗ trợ: ${assignmentsState.requestId}` : undefined}
          action={<Button size="small" onClick={reloadAssignments}>Thử lại</Button>}
        />
      )}

      {selectedAccountId != null && assignmentsState.status === "ready" && (
        <Card
          className="role-assignments-table-card"
          variant="borderless"
          role="region"
          aria-label="Danh sách vai trò & phạm vi"
          title="Danh sách vai trò & phạm vi"
        >
          {assignmentsState.page.data.length === 0 ? (
            <Empty description="Chưa có vai trò được cấp cho tài khoản này." />
          ) : (
            <RoleAssignmentTable
              items={assignmentsState.page.data}
              catalogRoles={catalog?.roles ?? []}
              orgNames={orgNames}
              areaNames={areaNames}
              canRevoke={canRevoke}
              onRevoke={(item) => setRevokeModal({ item, submitting: false, error: null })}
            />
          )}
        </Card>
      )}

      {grantModal != null && catalog != null && selectedAccount != null && (
        <GrantRoleAssignmentModal
          open
          accountLabel={`${selectedAccount.personDisplayName} · ${selectedAccount.username}`}
          catalog={catalog}
          orgOptions={orgNodes.filter((node) => node.status === "ACTIVE")
            .map((node) => ({ id: node.id, name: node.name }))}
          areaOptions={areaNodes.filter((node) => node.status === "ACTIVE")
            .map((node) => ({ id: node.id, name: node.name }))}
          submitting={grantModal.submitting}
          error={grantModal.error}
          onCancel={() => setGrantModal(null)}
          onSubmit={(values) => void handleGrantSubmit(values)}
        />
      )}

      {revokeModal != null && selectedAccount != null && (
        <RevokeRoleAssignmentModal
          open
          roleLabel={roleLabel(revokeModal.item.roleCode)}
          scopeLabel={scopeSummary(revokeModal.item, orgNames, areaNames)}
          periodLabel={periodLabel(revokeModal.item)}
          submitting={revokeModal.submitting}
          error={revokeModal.error}
          onCancel={() => setRevokeModal(null)}
          onConfirm={(reason) => void handleRevokeConfirm(reason)}
          onRefreshKeepingReason={reloadAssignments}
        />
      )}

      {drawerMode != null && selectedAccount != null && (
        <EffectivePermissionDrawer
          open
          mode={drawerMode}
          accountLabel={`${selectedAccount.personDisplayName} · ${selectedAccount.username}`}
          previewState={previewState}
          historyItems={assignmentsState.status === "ready" ? assignmentsState.page.data : []}
          catalogRoles={catalog?.roles ?? []}
          orgNames={orgNames}
          areaNames={areaNames}
          onClose={() => setDrawerMode(null)}
        />
      )}
    </div>
  );
}
